import { evaluateEntry, evaluateExpression, evaluateScientific } from '../utils/math';
import {
  appendToExpression,
  backspaceExpression,
  formatFormulaDisplay,
  getCurrentEntry,
  hasBinaryOperator,
  replaceCurrentOperand,
} from '../utils/displayFormat';
import {
  hasUnarySymbols,
  isUnaryWrappedOperand,
  type UnarySymbolKind,
  wrapUnarySymbol,
} from '../utils/unarySymbols';

export interface CalculatorState {
  expression: string;
  result: string;
  formulaLine: string;
  displayMain: string;
  memory: number;
  error: string;
}

export function createCalculatorState(scientific = false): CalculatorState {
  return {
    expression: '',
    result: '',
    formulaLine: '',
    displayMain: '0',
    memory: 0,
    error: '',
  };
}

export function createCalculator(scientific = false) {
  const state = createCalculatorState(scientific);

  function isOperatorToken(token: string): boolean {
    return token === '+' || token === '-' || token === '×' || token === '÷' || token === '^' || token === 'mod';
  }

  function autoClosePendingLogLn(raw: string): string {
    let s = raw;
    while (true) {
      const m = s.match(/(log10|ln)\(([^()]*)$/i);
      if (!m) break;
      const arg = m[2] ?? '';
      if (!arg) break;
      if (/[+\-×÷*/^]$/.test(arg) || /\bmod$/i.test(arg)) break;
      s += ')';
      break;
    }
    return s;
  }

  function formatNumber(n: number): string {
    return Number.isInteger(n) ? String(n) : String(n);
  }

  function previewEntryValue(expr: string): string {
    const entry = getCurrentEntry(expr);
    try {
      return formatNumber(evaluateEntry(entry, scientific));
    } catch {
      return entry;
    }
  }

  function syncDisplay() {
    if (state.error) return;
    const raw = state.expression;

    if (state.formulaLine.endsWith('=')) {
      state.displayMain = state.result || raw || '0';
      return;
    }

    if (!raw) {
      state.formulaLine = '';
      state.displayMain = '0';
      state.result = '';
      return;
    }

    if (hasBinaryOperator(raw) || /[()]/.test(raw) || hasUnarySymbols(raw)) {
      state.formulaLine = formatFormulaDisplay(raw);
      state.displayMain = previewEntryValue(raw);
      state.result = '';
    } else {
      state.formulaLine = '';
      state.displayMain = raw;
      state.result = '';
    }
  }

  function append(token: string) {
    state.error = '';
    if (state.formulaLine.endsWith('=')) {
      state.formulaLine = '';
      if (!isOperatorToken(token)) {
        state.expression = '';
      }
    }
    if (token === '10^') {
      state.expression = autoClosePendingLogLn(state.expression);
      const s = state.expression.replace(/\s+/g, '');
      if (!s || endsWithInfixOp(s) || s.endsWith('(')) {
        state.expression += '10^';
      } else {
        state.expression = replaceCurrentOperand(state.expression, '10^');
      }
    } else if (isOperatorToken(token)) {
      state.expression = autoClosePendingLogLn(state.expression);
      state.expression += token;
    } else if (/^[0-9.]$/.test(token)) {
      state.expression = appendToExpression(state.expression, token);
    } else {
      state.expression += token;
    }
    syncDisplay();
  }

  function clearAll() {
    Object.assign(state, createCalculatorState(scientific));
  }

  function clearEntry() {
    state.expression = '';
    state.formulaLine = '';
    state.displayMain = '0';
    state.result = '';
    state.error = '';
  }

  function backspace() {
    if (state.formulaLine.endsWith('=')) return;
    state.expression = backspaceExpression(state.expression);
    syncDisplay();
  }

  function toggleSign() {
    if (!state.expression) return;
    state.error = '';
    if (state.formulaLine.endsWith('=')) {
      if (state.expression.startsWith('-')) {
        state.expression = state.expression.slice(1);
      } else {
        state.expression = '-' + state.expression;
      }
      state.result = state.expression;
      state.displayMain = state.expression;
      return;
    }
    if (state.expression.startsWith('-')) {
      state.expression = state.expression.slice(1);
    } else {
      state.expression = '-' + state.expression;
    }
    syncDisplay();
  }

  function calculate() {
    try {
      const closed = autoClosePendingLogLn(state.expression);
      state.expression = closed;
      const val = scientific ? evaluateScientific(closed) : evaluateExpression(closed);
      state.result = String(val);
      state.formulaLine = `${formatFormulaDisplay(closed)} =`;
      state.displayMain = state.result;
      state.expression = state.result;
    } catch {
      state.error = 'invalid';
      state.result = '';
    }
  }

  function isChainedExpression(): boolean {
    const raw = state.expression.replace(/\s+/g, '');
    if (!raw) return false;
    const op = getCurrentEntry(state.expression);
    return op !== raw;
  }

  function currentOperand(): string {
    const raw = state.expression || '0';
    if (hasBinaryOperator(raw) || /[()]/.test(raw)) return getCurrentEntry(raw);
    return raw;
  }

  function endsWithInfixOp(raw: string): boolean {
    const s = raw.replace(/\s+/g, '');
    if (!s) return false;
    if (/[+\-×÷*/^]$/.test(s)) return true;
    return /\bmod$/i.test(s);
  }

  function applyUnarySymbolic(
    kind: UnarySymbolKind,
    validate?: (v: number) => void,
    divideByZero = false,
  ) {
    state.error = '';
    if (state.formulaLine.endsWith('=')) state.formulaLine = '';
    const raw = state.expression.replace(/\s+/g, '');
    if (endsWithInfixOp(raw)) return;
    const op = currentOperand();
    if (!op || isUnaryWrappedOperand(op)) return;
    try {
      const v = evaluateEntry(op, scientific);
      if (validate) validate(v);
      const symbol = wrapUnarySymbol(op, kind);
      if (isChainedExpression()) {
        state.expression = replaceCurrentOperand(state.expression, symbol);
      } else {
        state.expression = symbol;
        state.formulaLine = '';
      }
      syncDisplay();
    } catch (e) {
      if (divideByZero && e instanceof Error && e.message === 'div0') {
        state.error = 'divideByZero';
      } else if (e instanceof Error && e.message === 'overflow') {
        state.error = 'overflow';
      } else {
        state.error = 'invalid';
      }
    }
  }

  function canUnaryLogApply(): boolean {
    if (state.formulaLine.endsWith('=')) return !!state.expression;
    const s = state.expression.replace(/\s+/g, '');
    if (!s) return false;
    if (endsWithInfixOp(s)) return false;
    if (/^(log10|ln)\($/i.test(s)) return false;
    if (/\b(log10|ln)\([^)]*$/i.test(s)) return false;
    if (s.endsWith('(')) return false;
    const op = currentOperand();
    if (!op || isUnaryWrappedOperand(op)) return false;
    try {
      return Number.isFinite(evaluateEntry(op, scientific));
    } catch {
      return false;
    }
  }

  function memClear() {
    state.memory = 0;
  }
  function memRecall() {
    state.expression = String(state.memory);
    state.formulaLine = '';
    syncDisplay();
  }
  function memAdd() {
    try {
      state.memory += evaluateExpression(state.expression || '0');
    } catch {
      state.error = 'invalid';
    }
  }
  function memSub() {
    try {
      state.memory -= evaluateExpression(state.expression || '0');
    } catch {
      state.error = 'invalid';
    }
  }
  function memStore() {
    try {
      state.memory = evaluateExpression(state.expression || '0');
    } catch {
      state.error = 'invalid';
    }
  }

  function applyPercent() {
    try {
      const v = evaluateExpression(state.expression || '0') / 100;
      state.result = String(v);
      state.expression = state.result;
      state.formulaLine = '';
      state.displayMain = state.result;
      syncDisplay();
    } catch {
      state.error = 'invalid';
    }
  }

  return {
    state,
    syncDisplay,
    append,
    clearAll,
    clearEntry,
    backspace,
    toggleSign,
    calculate,
    applyPercent,
    applySqrt: () =>
      applyUnarySymbolic('sqrt', (v) => {
        if (v < 0) throw new Error('invalid');
      }),
    applySquare: () => applyUnarySymbolic('sqr'),
    applyReciprocal: () =>
      applyUnarySymbolic(
        'inv',
        (v) => {
          if (v === 0) throw new Error('div0');
        },
        true,
      ),
    applyFactorial: () =>
      applyUnarySymbolic('fact', (v) => {
        const n = Math.round(v);
        if (n < 0 || Math.abs(v - n) > 1e-10) throw new Error('invalid');
        if (n > 170) throw new Error('overflow');
      }),
    pressLog10: () => {
      state.error = '';
      if (state.formulaLine.endsWith('=')) state.formulaLine = '';
      const s = state.expression.replace(/\s+/g, '');
      if (/^log10\($/i.test(s)) return;
      if (canUnaryLogApply()) {
        applyUnarySymbolic('log10', (v) => {
          if (v <= 0) throw new Error('invalid');
        });
      } else {
        append('log10(');
      }
    },
    pressLn: () => {
      state.error = '';
      if (state.formulaLine.endsWith('=')) state.formulaLine = '';
      const s = state.expression.replace(/\s+/g, '');
      if (/^ln\($/i.test(s)) return;
      if (canUnaryLogApply()) {
        applyUnarySymbolic('ln', (v) => {
          if (v <= 0) throw new Error('invalid');
        });
      } else {
        append('ln(');
      }
    },
    memClear,
    memRecall,
    memAdd,
    memSub,
    memStore,
    autoClosePendingLogLn,
  };
}
